import { FarmRiskType } from '@ourbranch/lookups';

// #32526: farm mortgages are stored per-structure on `farmLocations[].risks[].building.mortgageDetails`
// (the representation the dec/docs read), not on the legacy flat `farm.mortgageDetails`. The staff-fe
// mortgage form keeps a single flat "Mortgage lender information" list.
//
// #34881: a single mortgagee can apply to MANY structures (e.g. every outbuilding on a policy with no
// dwelling), mirroring the Loss Payee / Additional Insured "Associated risks" multi-select. Each form
// entry carries a form-only `appliesToStructures` — an array of refs chosen in the "Applies to
// structures" chip picker (`locationId::riskId`, or `locationId::riskId::itemId` for a Related Private
// Structure). On save the entry fans out into each selected structure's `building.mortgageDetails`; on
// load, per-building records that share a lender + loan number are grouped back into one entry so the
// same lender never surfaces as duplicate rows. These helpers convert between that flat form list and
// per-building storage.

// Structures that can carry a mortgage: owned dwellings (FO-1/2/3) and FO-6 structures
// (barns/outbuildings). FO-4 renters (tenant contents) and scheduled/unscheduled inland marine
// are not mortgageable.
export const MORTGAGEABLE_RISK_TYPES = [
  FarmRiskType.Fo1,
  FarmRiskType.Fo2,
  FarmRiskType.Fo3,
  FarmRiskType.Fo6Structures
];

export const isMortgageableRiskType = (type) => MORTGAGEABLE_RISK_TYPES.includes(type);

// Composite id used by the "Applies to structure" dropdown and to route a mortgage back to its
// building. Mirrors the `locationId::riskId` convention used elsewhere on the farm form.
export const structureRefId = (locationId, riskId) => `${locationId}::${riskId}`;

// #32526 (reopen): a mortgage may instead apply to a specific Related Private Structure (RPS) —
// a pool, a shed — which is a schedule-coverage sub-item, not a risk. RPS items have no `building`
// of their own, so the mortgage is still stored on the parent risk's `building.mortgageDetails`; the
// chosen item id is persisted alongside it as `appliesToRelatedStructureId` so the association
// round-trips and prints on the dec. The dropdown identifies an RPS with a 3-part composite ref.
export const rpsRefId = (locationId, riskId, itemId) => `${locationId}::${riskId}::${itemId}`;

const RELATED_PRIVATE_STRUCTURES_COVERAGE_TYPE = 'RELATED_PRIVATE_STRUCTURES';

// The Related Private Structure items under a risk (schedule-coverage sub-items). Mirrors the source
// getRpsOptions reads in farm-helpers.js so the mortgage dropdown lists exactly the RPS section.
const getRiskRpsItems = (risk) =>
  (risk?.coverages?.scheduleCoverages || [])
    .filter((sc) => sc?.coverageType === RELATED_PRIVATE_STRUCTURES_COVERAGE_TYPE)
    .flatMap((sc) => sc.items || []);

// RPS item display label — same precedence as getRpsOptions (extendedDetails.name, then description).
const rpsItemLabel = (item) => item?.extendedDetails?.name || item?.description || item?.id;

/**
 * Builds the "Applies to structure" dropdown options for the mortgage form: the subset of the
 * given risk options that correspond to mortgageable structures (owned dwellings + FO-6 barns).
 * Shared by the offer and policy-details render paths.
 * @param {Array} farmLocations - the form's farmLocations
 * @param {Array} riskOptions - `getRiskOptions(...)` output ({ id: 'loc::risk', value })
 */
export const getMortgageStructureOptions = (farmLocations, riskOptions) => {
  // Map each mortgageable risk ref → its risk object so we can append that structure's RPS items
  // directly after it in the dropdown. Deleted locations are skipped.
  const mortgageableRisksByRef = new Map();
  for (const location of farmLocations || []) {
    if (location?.isDeleted) {
      continue;
    }
    for (const risk of location.risks || []) {
      if (isMortgageableRiskType(risk.type)) {
        mortgageableRisksByRef.set(structureRefId(location.id, risk.id), risk);
      }
    }
  }

  const options = [];
  for (const option of riskOptions || []) {
    const risk = mortgageableRisksByRef.get(option.id);
    if (!risk) {
      continue; // non-mortgageable structure (inland marine, FO-4 renters) or a deleted location
    }
    options.push(option);
    // #32526 (reopen): each Related Private Structure under this structure is selectable too, labeled
    // "<parent structure> — <RPS name>" and identified by a 3-part `loc::risk::item` ref.
    // #34881: flag RPS options excludeFromSelectAll so the chip picker's "Automatically associate to all
    // items" applies to whole structures only — otherwise it would also fan the mortgage onto every RPS
    // nested under a selected structure, writing redundant per-building records. RPS stays individually
    // selectable.
    for (const item of getRiskRpsItems(risk)) {
      options.push({
        id: `${option.id}::${item.id}`,
        value: `${option.value} — ${rpsItemLabel(item)}`,
        excludeFromSelectAll: true
      });
    }
  }
  return options;
};

const normalizeKeyPart = (value) => (value ?? '').toString().trim().toLowerCase();

// Identity used to group per-building mortgage records back into one form entry (#34881): a lender +
// loan number. The same mortgagee added to N structures is stored as N identical records (one per
// building); they share this key and collapse to a single row whose `appliesToStructures` lists every
// structure. Including the full billing address (not just the directory id) groups custom lenders too,
// which have no `mortgageHolderId`. Distinct loan numbers stay distinct entries. This also
// auto-consolidates the pre-#34881 workaround of adding the same lender once per structure.
const mortgageGroupKey = (mortgage) =>
  JSON.stringify([
    normalizeKeyPart(mortgage?.mortgageHolderId),
    normalizeKeyPart(mortgage?.mortgageHolderName),
    normalizeKeyPart(mortgage?.mortgageHolderAddress?.address),
    normalizeKeyPart(mortgage?.mortgageHolderAddress?.city),
    normalizeKeyPart(mortgage?.mortgageHolderAddress?.state),
    normalizeKeyPart(mortgage?.mortgageHolderAddress?.zip),
    normalizeKeyPart(mortgage?.loanNumber)
  ]);

/**
 * Flattens per-building mortgages into the form list, grouping records that share a lender + loan
 * number into ONE entry whose form-only `appliesToStructures` array lists every structure the lender
 * covers (in document order). Skips deleted locations. (#32526, #34881)
 *
 * A record tagged with `appliesToRelatedStructureId` contributes a 3-part RPS ref; an untagged one a
 * plain structure ref. The persisted tag is never surfaced on the form object — the chip picker drives
 * off `appliesToStructures` alone, and applyFarmMortgagesToLocations re-derives the tag from it. The
 * representative lender fields come from the first record seen for a group; `primary` is OR-ed across
 * the group so a primary designation on any building keeps the grouped entry primary.
 */
export const flattenFarmMortgagesForForm = (farmLocations) => {
  const groups = new Map();
  for (const location of farmLocations || []) {
    if (location?.isDeleted) {
      continue;
    }
    for (const risk of location.risks || []) {
      for (const mortgage of risk.building?.mortgageDetails || []) {
        const { appliesToRelatedStructureId, ...rest } = mortgage || {};
        const ref = appliesToRelatedStructureId
          ? rpsRefId(location.id, risk.id, appliesToRelatedStructureId)
          : structureRefId(location.id, risk.id);
        const key = mortgageGroupKey(mortgage);
        const existing = groups.get(key);
        if (existing) {
          if (!existing.appliesToStructures.includes(ref)) {
            existing.appliesToStructures.push(ref);
          }
          // Keep the entry primary if any of its buildings is — but never add a falsy `primary` key
          // to a group that has none, so a single-record entry round-trips byte-for-byte.
          if (rest.primary) {
            existing.primary = true;
          }
        } else {
          groups.set(key, { ...rest, appliesToStructures: [ref] });
        }
      }
    }
  }
  return Array.from(groups.values());
};

/**
 * Collects the mortgageable structures (owned dwellings + FO-6 barns) that carry a building, in
 * document order, tagging dwellings so callers can prefer them. Skips deleted locations.
 */
const collectMortgageableStructures = (farmLocations) => {
  const structures = [];
  for (const location of farmLocations || []) {
    if (location?.isDeleted) {
      continue;
    }
    for (const risk of location.risks || []) {
      if (risk?.building && isMortgageableRiskType(risk.type)) {
        structures.push({
          ref: structureRefId(location.id, risk.id),
          // FO-1/2/3 are dwellings; FO-6 structures (barns/outbuildings) are the fallback.
          isDwelling: risk.type !== FarmRiskType.Fo6Structures
        });
      }
    }
  }
  return structures;
};

// Given the collected mortgageable structures, the default a mortgage attaches to: the first owned
// dwelling, falling back to the first FO-6 structure. Mirrors the IMT importer's "first dwelling;
// fallback FO-6" convention. Null when there's no mortgageable structure.
const primaryRefFromStructures = (structures) =>
  (structures.find((structure) => structure.isDwelling) || structures[0])?.ref ?? null;

/**
 * The default structure a mortgage attaches to when the agent doesn't pick one (or picks a
 * structure that no longer exists): the first owned dwelling, falling back to the first FO-6
 * structure. Returns null when the farm has no mortgageable structure (e.g. an FO-4 renters-only
 * policy) — nowhere to place a mortgage.
 */
export const getPrimaryMortgageableStructureRef = (farmLocations) =>
  primaryRefFromStructures(collectMortgageableStructures(farmLocations));

/**
 * Distributes the flat form mortgage list back into each structure's `building.mortgageDetails`,
 * stripping the form-only `appliesToStructures`. Returns a NEW farmLocations array. Every building is
 * rewritten (buildings with no mortgage get an empty array) so removals and re-assignments persist.
 *
 * Each entry fans out into every structure named in its `appliesToStructures` array, writing an
 * identical mortgage record on each target building (#34881). A 3-part RPS ref lands on the parent
 * building tagged with `appliesToRelatedStructureId`; the tag is kept only when the item still exists
 * (a removed pool/shed drops it). Refs pointing at a structure that no longer exists are dropped from
 * the array.
 *
 * A mortgage is NEVER silently dropped: if an entry ends up with NO valid targets — an empty
 * `appliesToStructures`, or one where every ref is stale — it falls back to the primary mortgageable
 * structure (first dwelling, then FO-6). Previously such a mortgage was discarded, so a mortgagee an
 * agent added without touching the easily-missed structure picker vanished on the next Update Offer and
 * never printed (#32607). The only case where a mortgage can't be placed is a farm with no mortgageable
 * structure.
 */
export const applyFarmMortgagesToLocations = (farmLocations, flatMortgages) => {
  // Collect once; derive both the valid-ref set and the primary fallback from it.
  const structures = collectMortgageableStructures(farmLocations);
  const validRefs = new Set(structures.map((structure) => structure.ref));
  const primaryRef = primaryRefFromStructures(structures);

  // Valid RPS item ids per mortgageable risk ref — used to keep an RPS tag only when the item still
  // exists under the structure the mortgage lands on (a removed pool/shed drops the tag). (#32526)
  const rpsItemIdsByRiskRef = new Map();
  for (const location of farmLocations || []) {
    if (location?.isDeleted) {
      continue;
    }
    for (const risk of location.risks || []) {
      if (isMortgageableRiskType(risk.type)) {
        rpsItemIdsByRiskRef.set(
          structureRefId(location.id, risk.id),
          new Set(getRiskRpsItems(risk).map((item) => item.id))
        );
      }
    }
  }

  const byStructure = new Map();
  for (const mortgage of flatMortgages || []) {
    // Strip the form-only refs AND any stale persisted tag; the tag is re-derived from each ref below.
    const { appliesToStructures, appliesToStructure, appliesToRelatedStructureId: _staleTag, ...rest } = mortgage || {};

    // Tolerate a legacy singular `appliesToStructure` alongside the array so an in-flight form value
    // never drops a mortgage during the field rename.
    const refs = Array.isArray(appliesToStructures)
      ? appliesToStructures
      : appliesToStructure
        ? [appliesToStructure]
        : [];

    // Resolve each ref to a concrete { riskRef, rpsItemId } placement, keeping only refs whose risk
    // still exists. An RPS ref is `loc::risk::item`; a structure ref is `loc::risk`. The mortgage lives
    // on the risk's building either way — an RPS has no building — so route by the risk part.
    const placements = [];
    const seenPlacements = new Set();
    for (const ref of refs) {
      const parts = (ref || '').split('::');
      const riskRef = parts.length >= 2 ? structureRefId(parts[0], parts[1]) : '';
      if (!validRefs.has(riskRef)) {
        continue; // stale ref — its risk is gone
      }
      // Keep the RPS association only when the referenced item still exists under the target structure.
      const rpsItemId = parts.length >= 3 && rpsItemIdsByRiskRef.get(riskRef)?.has(parts[2]) ? parts[2] : '';
      // Two refs can resolve to the same building record (e.g. a plain ref and an RPS ref whose item is
      // already gone); dedupe so the fan-out doesn't write it twice.
      const placementKey = `${riskRef}::${rpsItemId}`;
      if (seenPlacements.has(placementKey)) {
        continue;
      }
      seenPlacements.add(placementKey);
      placements.push({ riskRef, rpsItemId });
    }

    // Never silently drop: an entry with no surviving target falls back to the primary structure.
    if (placements.length === 0) {
      if (!primaryRef) {
        continue; // No mortgageable structure exists — nowhere to attach it.
      }
      placements.push({ riskRef: primaryRef, rpsItemId: '' });
    }

    for (const { riskRef, rpsItemId } of placements) {
      const stored = rpsItemId ? { ...rest, appliesToRelatedStructureId: rpsItemId } : { ...rest };
      if (!byStructure.has(riskRef)) {
        byStructure.set(riskRef, []);
      }
      byStructure.get(riskRef).push(stored);
    }
  }

  return (farmLocations || []).map((location) => ({
    ...location,
    risks: (location.risks || []).map((risk) => {
      // Scheduled/unscheduled inland-marine risks have no building — leave them untouched.
      if (!risk.building) {
        return risk;
      }
      const ref = structureRefId(location.id, risk.id);
      return { ...risk, building: { ...risk.building, mortgageDetails: byStructure.get(ref) || [] } };
    })
  }));
};
